import { useState } from "react";

import { exportarExtrato } from "./api";
import { CategoriaErro, ErroHttp } from "../http/erros";

/*
 * Hook de AÇÃO imperativa do export de extrato (Gap C).
 *
 * NÃO é uma query/cache (TanStack): exportar é um efeito colateral de download,
 * não server-state cacheável. O hook mantém só `{ exportando, erro }` e expõe
 * `baixar(empresaId, horizonte)`.
 *
 * Fluxo do `baixar`:
 *  1. busca o blob via `exportarExtrato` (Bearer + refresh/401 do cliente);
 *  2. dispara o download nativo: `createObjectURL` → `<a download>` → `click()`;
 *  3. revoga o objectURL SEMPRE (em `finally` do bloco de URL), inclusive se o
 *     `click()` lançar — senão a URL fica viva e vaza memória.
 *  4. erro de rede/HTTP → mensagem AMIGÁVEL em PT-BR (nunca ecoa status cru nem
 *     corpo do servidor); `exportando` volta a `false` no `finally`.
 *
 * As mensagens espelham as categorias tipadas do `ErroHttp` (sem vazar motivo
 * real): 503 indisponível, 404 escopo/empresa, 422 requisição inválida, 401
 * sessão expirada; demais → genérica.
 */

/** Mensagens amigáveis por categoria — nunca ecoam status cru nem corpo. */
function mensagemAmigavel(erro: unknown): string {
  if (erro instanceof ErroHttp) {
    switch (erro.categoria) {
      case CategoriaErro.ModuloIndisponivel:
        return "Exportação indisponível no momento.";
      case CategoriaErro.NaoEncontrado:
        return "Empresa não encontrada para exportação.";
      case CategoriaErro.RequisicaoInvalida:
        return "Parâmetros de exportação inválidos.";
      case CategoriaErro.SessaoExpirada:
        return "Sessão expirada. Faça login novamente.";
      case CategoriaErro.SemPermissao:
        return "Você não tem permissão para exportar o extrato.";
      default:
        return "Não foi possível exportar o extrato.";
    }
  }
  return "Não foi possível exportar o extrato.";
}

/**
 * Dispara o download nativo do `blob` com o nome `filename`. Cria um objectURL,
 * um `<a download>` sintético, clica e — SEMPRE — revoga a URL no `finally`,
 * mesmo que o `click()` lance. O `<a>` nunca é anexado ao DOM (não precisa).
 */
function dispararDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const ancora = document.createElement("a");
    ancora.href = url;
    ancora.download = filename;
    ancora.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Estado e ação do export de extrato. */
export interface ExportExtrato {
  /** Há um download em andamento? Trava o botão e troca o label. */
  exportando: boolean;
  /** Mensagem amigável do último erro, ou `null` se não houve. */
  erro: string | null;
  /** Dispara o download do extrato CSV da empresa nos últimos `horizonte` dias. */
  baixar: (empresaId: string, horizonte: number) => Promise<void>;
}

export function useExportExtrato(): ExportExtrato {
  const [exportando, setExportando] = useState<boolean>(false);
  const [erro, setErro] = useState<string | null>(null);

  async function baixar(empresaId: string, horizonte: number): Promise<void> {
    setExportando(true);
    setErro(null);
    try {
      const { blob, filename } = await exportarExtrato(empresaId, horizonte);
      dispararDownload(blob, filename);
    } catch (causa) {
      setErro(mensagemAmigavel(causa));
    } finally {
      setExportando(false);
    }
  }

  return { exportando, erro, baixar };
}
